import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { APP_URL } from "@/lib/constants";
import { buildWhatsAppUrl } from "@/lib/utm";
import { StylistStatusBadge } from "@/components/landing/StylistStatusBadge";
import { trackEvent } from "@/lib/analytics";

const STATES = [
  {
    eyebrow: "Клуб привилегий Angiolini",
    title: "Ваш персональный гид\nв мир итальянской\nверхней одежды",
    sub: "Зарегистрируйтесь бесплатно и получите доступ к закрытому клубу Angiolini: бонусы, виртуальная ИИ-примерка, полный каталог, новые коллекции и персональные предложения.",
    cta: true,
    center: false,
    checks: ["Регистрация за 1 минуту", "10 000 ₸ сразу после регистрации", "Без скачивания", "Бесплатно"],
  },
  {
    eyebrow: "Что это",
    title: "Закрытый клуб\nсо своими правилами.",
    sub: "Бонусы, ранний доступ к коллекциям и персональные предложения — только для участников.",
    cta: false,
    center: false,
  },
  {
    eyebrow: "ИИ-примерка",
    title: "Примерка, которая\nначинается дома.",
    sub: "Загрузите фото — и увидите модель на себе ещё до визита в бутик.",
    cta: false,
    center: false,
  },
  {
    eyebrow: "Angiolini VIP",
    title: "",
    sub: "Ваш доступ уже готов.",
    cta: true,
    center: true,
  },
];

const BANDS = [0.22, 0.5, 0.78];

export const Hero = () => {
  const trackRef = useRef(null);
  const videoRef = useRef(null);
  const targetTime = useRef(0);
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const [reduced] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const [mobile] = useState(
    () => window.matchMedia("(max-width: 767px)").matches
  );

  useEffect(() => {
    if (reduced) return;
    const video = videoRef.current;
    const track = trackRef.current;
    if (!video || !track) return;

    let raf;
    let ready = false;

    const onMeta = () => {
      ready = true;
      video.currentTime = 0.01;
    };
    video.addEventListener("loadedmetadata", onMeta);
    if (video.readyState >= 1) onMeta();
    video.load();

    const onScroll = () => {
      const rect = track.getBoundingClientRect();
      const total = track.offsetHeight - window.innerHeight;
      const p = Math.min(1, Math.max(0, -rect.top / total));
      setProgress(p);
      setActive(p < BANDS[0] ? 0 : p < BANDS[1] ? 1 : p < BANDS[2] ? 2 : 3);
      if (ready && video.duration) {
        targetTime.current = p * (video.duration - 0.05);
      }
    };

    const loop = () => {
      if (ready && video.duration && !video.seeking) {
        const diff = targetTime.current - video.currentTime;
        if (Math.abs(diff) > 0.01) {
          video.currentTime = video.currentTime + diff * 0.22;
        }
      }
      raf = requestAnimationFrame(loop);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    raf = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener("scroll", onScroll);
      video.removeEventListener("loadedmetadata", onMeta);
      cancelAnimationFrame(raf);
    };
  }, [reduced]);

  return (
    <section
      id="top"
      ref={trackRef}
      data-testid="hero-section"
      className="relative"
      style={{ height: reduced ? "100vh" : "440vh", background: "var(--ink)" }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {reduced ? (
          <img
            src={mobile ? "/media/poster-mobile.jpg" : "/media/poster.jpg"}
            alt="Angiolini — итальянская шуба"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <video
            key={mobile ? "m" : "d"}
            ref={videoRef}
            data-testid="hero-video"
            className="absolute inset-0 w-full h-full object-cover"
            poster={mobile ? "/media/poster-mobile.jpg" : "/media/poster.jpg"}
            muted
            playsInline
            preload="auto"
          >
            <source
              src={mobile ? "/media/hero-scrub-mobile.mp4" : "/media/hero-scrub.mp4"}
              type='video/mp4; codecs="avc1.640028"'
            />
            <source
              src={mobile ? "/media/hero-scrub-mobile.webm" : "/media/hero-scrub.webm"}
              type="video/webm"
            />
          </video>
        )}

        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(6,4,10,0.55) 0%, rgba(6,4,10,0.05) 34%, rgba(6,4,10,0.05) 55%, rgba(6,4,10,0.72) 100%)",
          }}
        />

        <div className="pw-container relative h-full pb-28 pt-24 md:pb-32">
          {STATES.map((s, i) => (
            <div
              key={i}
              data-testid={`hero-state-${i}`}
              className={`hero-state ${s.center ? "is-center" : ""} ${
                (reduced ? 0 : active) === i ? "is-active" : ""
              }`}
            >
              <span className="pw-eyebrow !text-white/60 mb-5">{s.eyebrow}</span>
              {s.title && (
                <h1
                  className="text-white font-semibold whitespace-pre-line"
                  style={{
                    fontSize: "clamp(2rem, 4.9vw, 4.25rem)",
                    letterSpacing: "-0.045em",
                    lineHeight: 1.04,
                  }}
                >
                  {s.title}
                </h1>
              )}
              <p className="mt-5 text-white/70 text-sm md:text-base max-w-xl font-normal leading-relaxed">
                {s.sub}
              </p>
              {s.cta && (
                <div className="mt-7 flex flex-wrap items-center gap-4">
                  <a
                    href={APP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`hero-cta-primary-${i}`}
                    onClick={() => trackEvent("app_click", { location: "hero" })}
                    className="pw-btn pw-btn-light"
                  >
                    Открыть приложение
                  </a>
                  <a
                    href={buildWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`hero-cta-whatsapp-${i}`}
                    onClick={(e) => {
                      e.currentTarget.href = buildWhatsAppUrl();
                      trackEvent("whatsapp_click", { location: "hero" });
                    }}
                    className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium text-white transition-all duration-300 hover:scale-[1.02]"
                    style={{
                      background: "#25D366",
                      boxShadow: "0 8px 24px rgba(37,211,102,0.35)",
                    }}
                  >
                    <svg viewBox="0 0 32 32" width="18" height="18" fill="currentColor" aria-hidden="true">
                      <path d="M19.11 17.32c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.5-.17-.01-.37-.01-.57-.01-.2 0-.52.07-.79.37s-1.04 1.02-1.04 2.49 1.07 2.89 1.22 3.09c.15.2 2.1 3.21 5.08 4.5.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35zM16.03 26.34h-.01c-1.9 0-3.77-.51-5.4-1.48l-.39-.23-4.02 1.05 1.07-3.92-.25-.4c-1.06-1.69-1.62-3.64-1.62-5.65 0-5.85 4.76-10.6 10.62-10.6 2.83 0 5.5 1.1 7.5 3.11 2 2 3.11 4.67 3.11 7.5-.01 5.86-4.77 10.62-10.61 10.62zM27.06 4.94A15.42 15.42 0 0016.03 0C7.34 0 .28 7.06.28 15.75c0 2.78.73 5.49 2.11 7.87L.15 32l8.61-2.26a15.7 15.7 0 007.27 1.85h.01c8.68 0 15.75-7.06 15.75-15.75 0-4.21-1.64-8.16-4.73-11.13z" />
                    </svg>
                    <span>Написать в WhatsApp</span>
                  </a>
                  <a
                    href="#features"
                    data-testid={`hero-cta-secondary-${i}`}
                    className="pw-btn pw-btn-ghost"
                  >
                    Смотреть возможности
                  </a>
                </div>
              )}
              {s.cta && (
                <div className="mt-4">
                  <StylistStatusBadge />
                </div>
              )}
              {s.checks && (
                <div data-testid="hero-checks" className="mt-6 grid grid-cols-2 md:flex md:flex-wrap gap-x-6 gap-y-2">
                  {s.checks.map((c) => (
                    <span key={c} className="flex items-center gap-2 text-[12px] md:text-[13px] text-white/70">
                      <Check size={14} strokeWidth={2.5} style={{ color: "var(--accent)" }} className="shrink-0" />
                      {c}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}

          {!reduced && (
            <>
              <div
                data-testid="hero-progress"
                className="absolute right-6 md:right-12 top-1/2 -translate-y-1/2 hidden sm:flex flex-col items-center gap-3"
              >
                <span className="text-white/50 text-[10px] font-semibold tracking-[0.2em]">
                  {String(Math.round(progress * 100)).padStart(2, "0")}
                </span>
                <div className="w-[2px] h-32 bg-white/15 rounded-full overflow-hidden">
                  <div
                    className="w-full bg-white rounded-full"
                    style={{ height: `${progress * 100}%` }}
                  />
                </div>
              </div>

              <div
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-opacity duration-500"
                style={{ opacity: progress > 0.04 ? 0 : 1 }}
              >
                <span className="text-white/50 text-[10px] font-semibold tracking-[0.22em] uppercase">
                  Листайте
                </span>
                <div className="w-[2px] h-10 overflow-hidden">
                  <div className="hint-line w-full h-full bg-white/60" />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
